import React, { useContext, useState, useEffect } from 'react';
import { Paper, TextField, Button, Select, MenuItem, Grid, Typography } from '@material-ui/core';
import { DataTable, Notifications } from '../../../../shared';
import { UserContext } from '../../../../contexts/UserContext';
import { fbmApi as api } from '../../../../api/fbm';
import { warehouseApi } from '../../../../api/warehouse';

const ViewExternalSkuUsage = () => {
    let [rows, setRows] = useState([]);
    let [notification, setNotification] = useState({ text: null, level: null });
    let [reportButtonText, setReportButtonText] = useState('Run Report');
    let [facilityID, setFacilityID] = useState(''); // Initialize to empty string for no selection
    let [facilityOptions, setFacilityOptions] = useState([]);
    const user = useContext(UserContext);

    let { fbmAccount } = useContext(UserContext);

    useEffect(() => {
        const fetchFacilities = async () => {
            if (fbmAccount && fbmAccount.facilityIDs && fbmAccount.facilityIDs.length > 0) {
                try {
                    const res = await warehouseApi.getFacilities(true); // Fetch active facilities
                    if (res.status === true) {
                        let whitelisted = res.data.rows.filter((fac) => fbmAccount.facilityIDs.includes(fac.id));
                        const options = [
                            { text: 'All Facilities', value: 0 },
                            ...whitelisted.map((fac) => ({ text: fac.name, value: fac.id })),
                        ];
                        setFacilityOptions(options);
                    } else {
                        setNotification({ text: 'Could not load facilities: ' + res.msg, level: 'error' });
                        setFacilityOptions([{ text: 'All Facilities', value: 0 }]);
                    }
                } catch (error) {
                    setNotification({ text: 'Error fetching facilities: ' + error.message, level: 'error' });
                    setFacilityOptions([{ text: 'All Facilities', value: 0 }]);
                }
            } else if (fbmAccount) {
                // Case where fbmAccount might exist but has no facilityIDs
                setFacilityOptions([{ text: 'All Facilities', value: 0 }]);
            }
        };

        fetchFacilities();
    }, [fbmAccount]);

    let handleRunReportClick = (event) => {
        getAndRenderReportData();
    };

    let columns = [
        { accessor: 'externalSku', Header: 'External Sku' },
        { accessor: 'itemName', Header: 'Item Name' },
        { accessor: 'averageUnitsPackedPerDay', Header: 'Average Units Packed Per Day' },
    ];

    let getAndRenderReportData = async () => {
        if (facilityID === '') {
            setNotification({ text: 'Please select a facility option.', level: 'warning' });
            return;
        }
        setReportButtonText('Loading...');
        setRows([]);
        setNotification({ text: null, level: null }); // Clear previous notifications

        // Use facilityID (0 for all, or specific ID)
        let response = await api.getReportExternalSkuUsageView(fbmAccount.fbmAccountName, facilityID);

        if (response.status === false) {
            setNotification({ text: 'No entries found ' + response.msg, level: 'error' });
            setRows([]);
        } else {
            setRows(response.data.rows);
            if (response.data.rows.length === 0) {
                setNotification({ text: 'No data found for the selected criteria.', level: 'info' });
            }
        }
        setReportButtonText('Run Report');
    };

    // Removed useEffect that runs report on fbmAccount load

    return (
        <Paper style={{ padding: '16px' }}>
            <Notifications options={notification} />

            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                    <Typography variant="h6">
                        {fbmAccount.companyName} - Units Packed per Day (over last 90 days from today)
                    </Typography>
                </Grid>
                <Grid item>
                    <Select
                        value={facilityID}
                        onChange={(event) => {
                            setFacilityID(event.target.value);
                            // Clear rows and notification when selection changes, prompting a new report run
                            setRows([]);
                            setNotification({ text: null, level: null });
                        }}
                        displayEmpty
                        variant="outlined"
                        style={{ minWidth: 200 }}
                    >
                        <MenuItem value="" disabled>
                            Select Facility
                        </MenuItem>
                        {facilityOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.text}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <Grid item>
                    <Button
                        id="runReport"
                        onClick={handleRunReportClick}
                        color="secondary"
                        variant="contained"
                        disabled={facilityID === '' || reportButtonText === 'Loading...'}
                    >
                        {reportButtonText}
                    </Button>
                </Grid>
            </Grid>

            {rows.length === 0 && reportButtonText !== 'Loading...' && facilityID === '' && (
                <Typography variant="body1" style={{ marginTop: '16px' }}>
                    Please select a facility option and click 'Run Report'.
                </Typography>
            )}
            {rows.length === 0 && reportButtonText !== 'Loading...' && facilityID !== '' && !notification.text && (
                <Typography variant="body1" style={{ marginTop: '16px' }}>
                    Click 'Run Report' to fetch data for the selected facility.
                </Typography>
            )}

            <Typography variant="caption" display="block" style={{ marginTop: '16px' }}>
                Note: Report may take up to 2 minutes to run.
            </Typography>

            <br />

            <DataTable
                csvExport={rows.length > 0 && reportButtonText === 'Run Report'}
                csvExportFileName="external-sku-usage"
                csvExportHeadersUseAccessor={true}
                columns={columns}
                data={rows}
                editable={false}
            />
        </Paper>
    );
};

export { ViewExternalSkuUsage };
